import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { OverviewStatsDto } from '../../services/api';
import { TrendingUp, TrendingDown, Users, Heart, Star, MessageCircle, Activity } from 'lucide-react';

interface OverviewCardsProps {
  data: OverviewStatsDto;
}

interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  icon: React.ReactNode;
  description?: string;
}

function StatCard({ title, value, change, icon, description }: StatCardProps) {
  const isNegative = change?.startsWith('-');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="h-4 w-4 text-muted-foreground">
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {change && (
          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
            {isNegative ? <TrendingDown className="h-3 w-3 text-red-600" /> : <TrendingUp className="h-3 w-3 text-green-600" />}
            <span className={isNegative ? 'text-red-600' : 'text-green-600'}>
              {change}
            </span>
            <span>vs last week</span>
          </div>
        )}
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function OverviewCards({ data }: OverviewCardsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
      <StatCard
        title="Total Prompts"
        value={data.totalPrompts}
        change={data.totalPromptsChange}
        icon={<Activity />}
        description="Active prompts in library"
      />

      <StatCard
        title="Total Users"
        value={data.totalUsers}
        change={data.totalUsersChange}
        icon={<Users />}
        description="Unique contributors"
      />
      
      <StatCard
        title="Total Uses"
        value={data.totalUses}
        change={data.totalUsesChange}
        icon={<TrendingUp />}
        description="Prompt executions"
      />
      
      <StatCard
        title="Total Likes"
        value={data.totalLikes}
        change={data.totalLikesChange}
        icon={<Heart />}
        description="Community appreciation"
      />
      
      <StatCard
        title="Average Rating"
        value={data.averageRating.toFixed(1)}
        change={data.averageRatingChange}
        icon={<Star />}
        description="Quality score"
      />
      
      <StatCard
        title="Comments"
        value={data.totalComments}
        change={data.totalCommentsChange}
        icon={<MessageCircle />}
        description="Community feedback"
      />
      
      <Card className="col-span-1 md:col-span-2 lg:col-span-4 xl:col-span-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Engagement</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Uses/Prompt</span>
              <span className="font-medium">
                {data.totalPrompts > 0 ? (data.totalUses / data.totalPrompts).toFixed(1) : '0'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Likes/Prompt</span>
              <span className="font-medium">
                {data.totalPrompts > 0 ? (data.totalLikes / data.totalPrompts).toFixed(1) : '0'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Engagement</span>
              <span className="font-medium">
                {data.totalUses > 0 ? ((data.totalLikes / data.totalUses) * 100).toFixed(1) : '0'}%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}